import React, { type ReactNode, useCallback, useContext, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import ButtonItem from '@atlaskit/menu/button-item';
import LinkItem from '@atlaskit/menu/link-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { Popup } from '@atlaskit/popup/popup';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';
import { layers } from '@atlaskit/theme/constants';

import { firedAt } from '../../analytics';
import { useCardOpenTimer } from '../../store/cardOpenTimer';
import { UserProfileCardContext } from '../../store/context';

const messages = defineMessages({
	actionMenuLabel: {
		id: 'user-profile-card.ActionMenu.actionMenuLabel',
		defaultMessage: 'More actions for {name}',
		description: 'Accessible label for the more-actions icon button on the profile card header',
	},
});

/**
 * Click handler signature shared by both menu-item variants. Accepts trailing rest args for
 * parity with the legacy `@atlaskit/profilecard` `ProfileCardAction.callback` signature, even
 * though `@atlaskit/menu`'s `ButtonItem`/`LinkItem` only emit `(event)` today.
 */
type ActionMenuItemClickHandler = (
	event: React.MouseEvent | React.KeyboardEvent,
	...args: any[]
) => void;

/**
 * Internal descriptor for a single action-menu item. `kind: 'link'` renders as `LinkItem` and
 * navigates via href on activation (unless `onClick` runs and calls `preventDefault`).
 * `kind: 'button'` renders as `ButtonItem` and only invokes `onClick`.
 */

export type ActionMenuItem =
	| {
			kind: 'button';
			id: string;
			label: ReactNode;
			onClick: ActionMenuItemClickHandler;
	  }
	| {
			kind: 'link';
			id: string;
			label: ReactNode;
			href: string;
			target?: string;
			/** When provided, called *before* navigation; calling `preventDefault` suppresses the link. */
			onClick?: ActionMenuItemClickHandler;
	  };

interface ActionMenuProps {
	/**
	 * List of menu items to render inside the popup. Items are rendered in order in a single
	 * section. The component returns `null` when this is empty.
	 */
	items: ActionMenuItem[];
	/** Subject's display name, used for the accessible label on the trigger button. */
	name: string;
}

export function ActionMenu({ items, name }: ActionMenuProps): React.JSX.Element | null {
	const { formatMessage } = useIntl();
	const [isOpen, setOpen] = useState(false);
	const { fireEvent } = useAnalyticsEvents();
	const { getDurationSinceInteraction } = useCardOpenTimer();
	const { getTestId } = useContext(UserProfileCardContext);

	// Stable callbacks so the inner `Popup` and `IconButton` don't see prop churn each render.
	const handleClose = useCallback(() => setOpen(false), []);
	const handleTriggerClick = useCallback(() => setOpen((open) => !open), []);

	const fireActionClick = useCallback(
		(itemId: string, hasHref: boolean, hasOnClick: boolean, index: number) => {
			fireEvent('ui.profilecard.clicked.action', {
				firedAt: firedAt(),
				duration: getDurationSinceInteraction() ?? 0,
				hasHref,
				hasOnClick,
				index,
				actionId: itemId,
			});
		},
		[fireEvent, getDurationSinceInteraction],
	);

	if (items.length === 0) {
		return null;
	}

	const renderItem = (item: ActionMenuItem, index: number) => {
		if (item.kind === 'link') {
			return (
				<LinkItem
					key={item.id}
					href={item.href}
					target={item.target}
					rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
					testId={getTestId(`menu-item-${item.id}`)}
					onClick={(event: React.MouseEvent | React.KeyboardEvent) => {
						fireActionClick(item.id, true, Boolean(item.onClick), index);
						item.onClick?.(event);
						setOpen(false);
					}}
				>
					{item.label}
				</LinkItem>
			);
		}
		return (
			<ButtonItem
				key={item.id}
				testId={getTestId(`menu-item-${item.id}`)}
				onClick={(event: React.MouseEvent | React.KeyboardEvent) => {
					fireActionClick(item.id, false, true, index);
					item.onClick(event);
					setOpen(false);
				}}
			>
				{item.label}
			</ButtonItem>
		);
	};

	return (
		<Popup
			isOpen={isOpen}
			onClose={handleClose}
			placement="right-start"
			// The host card sits at `layers.modal()` (510) — use `layers.tooltip()` (700) so the
			// action-menu popup stacks above it. `tooltip()` is the conventional next-layer-up in
			// ADS and is forward-compatible with the upcoming top-layer popup mode.
			zIndex={layers.tooltip()}
			testId={getTestId('action-menu')}
			content={() => (
				<MenuGroup spacing="cozy">
					<Section>{items.map(renderItem)}</Section>
				</MenuGroup>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					icon={MoreIcon}
					label={formatMessage(messages.actionMenuLabel, { name })}
					onClick={handleTriggerClick}
					isSelected={isOpen}
					testId={getTestId('action-menu-trigger')}
				/>
			)}
		/>
	);
}
